/**
 * Hardcoded Values Check Module
 * Detects magic numbers, hardcoded strings, URLs, etc.
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Magic number exceptions (common acceptable values)
const ACCEPTABLE_NUMBERS = new Set([
  0, 1, -1, 2, 10, 100, 1000,
  // Common sizes
  8, 16, 32, 64, 128, 256, 512, 1024,
  // HTTP status codes (often used as comparisons)
  200, 201, 204, 400, 401, 403, 404, 500,
]);

// Patterns for hardcoded values
const HARDCODED_URL_PATTERN = /["'](https?:\/\/(?!localhost)[^"']+)["']/gi;
const HARDCODED_EMAIL_PATTERN = /["']([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})["']/gi;
const HARDCODED_IP_PATTERN = /["'](\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})["']/gi;

// Timeout patterns (suspicious hardcoded timeouts)
const TIMEOUT_PATTERNS = [
  { pattern: /timeout\s*[=:]\s*(\d{4,})/gi, name: 'Timeout value' },
  { pattern: /TimeSpan\.FromSeconds\s*\(\s*(\d+)\s*\)/gi, name: 'TimeSpan.FromSeconds' },
  { pattern: /TimeSpan\.FromMinutes\s*\(\s*(\d+)\s*\)/gi, name: 'TimeSpan.FromMinutes' },
  { pattern: /setTimeout\s*\(\s*[^,]+,\s*(\d{4,})\s*\)/gi, name: 'setTimeout' },
  { pattern: /delay\s*[=:]\s*(\d{4,})/gi, name: 'Delay value' },
];

// Magic number pattern (standalone numbers in code)
const MAGIC_NUMBER_PATTERN = /(?<![a-zA-Z0-9_.])\b(\d+)\b(?![a-zA-Z0-9_])/g;

// Error message patterns - reserved for future use
// ERROR_MESSAGE_PATTERNS would detect hardcoded exception messages

// Feature flag patterns
const FEATURE_FLAG_PATTERNS = [
  { pattern: /if\s*\(\s*(true|false)\s*\)/gi, name: 'Hardcoded boolean condition' },
  { pattern: /enabled\s*[=:]\s*(true|false)/gi, name: 'Hardcoded enabled flag' },
];

export async function checkHardcodedValues(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    // Skip config, test, and constant files
    if (isConfigOrConstFile(file.relativePath)) continue;

    const lines = file.content.split('\n');

    // Check for hardcoded URLs
    let match;
    HARDCODED_URL_PATTERN.lastIndex = 0;
    while ((match = HARDCODED_URL_PATTERN.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);
      const lineContent = lines[lineNumber - 1]?.trim() || '';

      // Skip if in comments or appears to be a doc/example
      if (isInComment(lineContent) || isDocExample(lineContent)) continue;

      findings.push({
        id: generateFindingId('hardcoded-values'),
        category: 'hardcoded-values',
        severity: 'warning',
        title: 'Hardcoded URL',
        description: `Found hardcoded URL: ${truncateCode(match[1], 50)}. URLs should be in configuration.`,
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lineContent),
        suggestion: 'Move this URL to appsettings.json, environment variables, or a configuration service.',
        autoFixable: false,
      });
    }

    // Check for hardcoded emails
    HARDCODED_EMAIL_PATTERN.lastIndex = 0;
    while ((match = HARDCODED_EMAIL_PATTERN.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);
      const lineContent = lines[lineNumber - 1]?.trim() || '';

      if (isInComment(lineContent) || isDocExample(lineContent)) continue;

      // Skip test emails
      if (match[1].includes('@test') || match[1].includes('@example')) continue;

      findings.push({
        id: generateFindingId('hardcoded-values'),
        category: 'hardcoded-values',
        severity: 'warning',
        title: 'Hardcoded Email',
        description: `Found hardcoded email: ${match[1]}. Consider using configuration.`,
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lineContent),
        suggestion: 'Move this email to configuration or use a configurable notification service.',
        autoFixable: false,
      });
    }

    // Check for hardcoded IP addresses
    HARDCODED_IP_PATTERN.lastIndex = 0;
    while ((match = HARDCODED_IP_PATTERN.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);
      const lineContent = lines[lineNumber - 1]?.trim() || '';

      if (isInComment(lineContent)) continue;

      // Skip localhost and common test IPs
      if (match[1] === '127.0.0.1' || match[1] === '0.0.0.0') continue;

      findings.push({
        id: generateFindingId('hardcoded-values'),
        category: 'hardcoded-values',
        severity: 'warning',
        title: 'Hardcoded IP Address',
        description: `Found hardcoded IP address: ${match[1]}. This should be configurable.`,
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lineContent),
        suggestion: 'Move this IP address to configuration.',
        autoFixable: false,
      });
    }

    // Check for timeout patterns
    for (const { pattern, name } of TIMEOUT_PATTERNS) {
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const value = parseInt(match[1], 10);
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        if (isInComment(lineContent)) continue;

        // Only flag if not a small/common value
        if (value > 1000 || (value > 60 && name.includes('Minutes'))) {
          findings.push({
            id: generateFindingId('hardcoded-values'),
            category: 'hardcoded-values',
            severity: 'warning',
            title: 'Hardcoded Timeout',
            description: `${name} with hardcoded value ${value}. Consider making timeouts configurable.`,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Extract timeout values to configuration for easier adjustment in different environments.',
            autoFixable: false,
          });
        }
      }
    }

    // Check for magic numbers (only in business logic files)
    if (isBusinessLogicFile(file.relativePath) && file.language === 'csharp') {
      checkMagicNumbers(file.content, file.relativePath, lines, findings);
    }

    // Check for hardcoded feature flags
    for (const { pattern, name } of FEATURE_FLAG_PATTERNS) {
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        if (isInComment(lineContent)) continue;

        // Skip obvious debug/test code
        if (lineContent.includes('DEBUG') || lineContent.includes('TEST')) continue;

        findings.push({
          id: generateFindingId('hardcoded-values'),
          category: 'hardcoded-values',
          severity: 'warning',
          title: 'Hardcoded Feature Flag',
          description: `${name} detected. Feature toggles should be configurable, not hardcoded.`,
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Use a feature flag system or configuration service instead of hardcoded boolean values.',
          autoFixable: false,
        });
      }
    }
  }

  return findings;
}

function checkMagicNumbers(
  content: string,
  filePath: string,
  lines: string[],
  findings: CodeReviewFinding[]
): void {
  const magicNumbersFound = new Map<number, number[]>(); // value -> line numbers

  let match;
  MAGIC_NUMBER_PATTERN.lastIndex = 0;
  while ((match = MAGIC_NUMBER_PATTERN.exec(content)) !== null) {
    const value = parseInt(match[1], 10);
    const lineNumber = getLineNumber(content, match.index);
    const lineContent = lines[lineNumber - 1]?.trim() || '';

    // Skip acceptable numbers
    if (ACCEPTABLE_NUMBERS.has(value)) continue;

    // Skip if in comment
    if (isInComment(lineContent)) continue;

    // Skip if part of a constant definition
    if (/const\s+\w+\s*=/.test(lineContent) || /readonly\s+\w+\s*=/.test(lineContent)) continue;

    // Skip array indices and simple comparisons
    if (/\[\s*\d+\s*\]/.test(lineContent)) continue;

    // Track magic numbers
    if (!magicNumbersFound.has(value)) {
      magicNumbersFound.set(value, []);
    }
    magicNumbersFound.get(value)!.push(lineNumber);
  }

  // Report magic numbers that appear multiple times (more suspicious)
  for (const [value, lineNumbers] of magicNumbersFound) {
    if (lineNumbers.length >= 2) {
      findings.push({
        id: generateFindingId('hardcoded-values'),
        category: 'hardcoded-values',
        severity: 'warning',
        title: 'Magic Number',
        description: `Magic number ${value} appears ${lineNumbers.length} times. Consider extracting to a named constant.`,
        file: filePath,
        line: lineNumbers[0],
        suggestion: `Extract ${value} to a well-named constant that explains its purpose.`,
        autoFixable: true,
      });
    }
  }
}

// Helper functions
function isConfigOrConstFile(filePath: string): boolean {
  const lower = filePath.toLowerCase();
  return (
    lower.includes('config') ||
    lower.includes('constant') ||
    lower.includes('settings') ||
    lower.includes('appsettings') ||
    lower.includes('.json') ||
    lower.includes('test') ||
    lower.includes('.spec.') ||
    lower.includes('.test.')
  );
}

function isBusinessLogicFile(filePath: string): boolean {
  const lower = filePath.toLowerCase();
  return (
    lower.includes('service') ||
    lower.includes('handler') ||
    lower.includes('controller') ||
    lower.includes('validator')
  );
}

function isDocExample(line: string): boolean {
  // Check for documentation patterns
  if (line.includes('/// ') || line.includes('* ')) return true;

  // Check for example keywords but not in URLs (example.com, example.org)
  const hasExampleKeyword = /\bexample\b/i.test(line);
  const isExampleInUrl = /example\.(com|org|net)/i.test(line);

  return hasExampleKeyword && !isExampleInUrl;
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}

function isInComment(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*');
}
